'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var _extends = require('@babel/runtime/helpers/extends');
var React = require('react');
var mustache = require('mustache');
var nthHookTranslations = require('@narvar/nth-hook-translations');
var nthHookContextPage = require('@narvar/nth-hook-context-page');
var nthHookTheme = require('@narvar/nth-hook-theme');
var useFormatEddQuery = require('@narvar/nth-hook-format-edd');
var Panel = require('@narvar/nth-block-panel');
var PanelHeader = require('@narvar/nth-bit-panel-header');
var nthKitDynamicLayoutNext = require('@narvar/nth-kit-dynamic-layout-next');
var nthHookContextAnalytics = require('@narvar/nth-hook-context-analytics');
var Carousel = require('@narvar/nth-bundle-carousel');
var useOrderTracking = require('@narvar/nth-hook-order-tracking');
var ShipmentRow = require('./components/ShipmentRow.js');
var PrefetchData = require('./PrefetchData.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var _extends__default = /*#__PURE__*/_interopDefaultLegacy(_extends);
var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var mustache__default = /*#__PURE__*/_interopDefaultLegacy(mustache);
var useFormatEddQuery__default = /*#__PURE__*/_interopDefaultLegacy(useFormatEddQuery);
var Panel__default = /*#__PURE__*/_interopDefaultLegacy(Panel);
var PanelHeader__default = /*#__PURE__*/_interopDefaultLegacy(PanelHeader);
var Carousel__default = /*#__PURE__*/_interopDefaultLegacy(Carousel);
var useOrderTracking__default = /*#__PURE__*/_interopDefaultLegacy(useOrderTracking);

const renderOptions = {
  escape: x => x
};
const {
  FeatureLayout
} = nthKitDynamicLayoutNext.LayoutTree;
const getShipmentsToRender = (shipments, mainShipmentId, trackingType) => {
  if (!shipments || shipments.length === 1) return [];
  // show all shipments, including current shipment for returns
  if (trackingType === 'RETURNS') return shipments;
  if (!mainShipmentId) return [];

  // Since the Shipments block shows "Other Shipments in Your Order",
  // filter out the current shipment based on shipment id
  return shipments.filter(({
    shipmentId
  }) => shipmentId !== mainShipmentId);
};
const eddFormatTranslationKeys = [{
  lastDay: 'packShipmentsDayOfWeekLastDayDateFormat',
  lastWeek: 'packShipmentsDayOfWeekLastWeekDateFormat',
  nextDay: 'packShipmentsDayOfWeekNextDayDateFormat',
  nextWeek: 'packShipmentsDayOfWeekNextWeekDateFormat',
  sameDay: 'packShipmentsDayOfWeekSameDayDateFormat',
  sameElse: 'packShipmentsDayOfWeekSameElseDateFormat'
}, {
  lastDay: 'packShipmentsMonthLastDayDateFormat',
  lastWeek: 'packShipmentsMonthLastWeekDateFormat',
  nextDay: 'packShipmentsMonthNextDayDateFormat',
  nextWeek: 'packShipmentsMonthNextWeekDateFormat',
  sameDay: 'packShipmentsMonthSameDayDateFormat',
  sameElse: 'packShipmentsMonthSameElseDateFormat'
}, {
  lastDay: 'packShipmentsDayOfMonthLastDayDateFormat',
  lastWeek: 'packShipmentsDayOfMonthLastWeekDateFormat',
  nextDay: 'packShipmentsDayOfMonthNextDayDateFormat',
  nextWeek: 'packShipmentsDayOfMonthNextWeekDateFormat',
  sameDay: 'packShipmentsDayOfMonthWeekSameDayDateFormat',
  sameElse: 'packShipmentsDayOfMonthSameElseDateFormat'
}];
const Shipments = ({
  featureName,
  layout = FeatureLayout.PORTRAIT,
  fixedPanelHeight
}) => {
  const landscape = layout === FeatureLayout.LANDSCAPE;
  const logEvent = nthHookContextAnalytics.useAnalyticsPost();
  const {
    panelcontainerNoneNone: containerStyle
  } = nthHookTheme.useTheme();
  const {
    state: {
      orderNumbers,
      trackingType
    }
  } = nthHookContextPage.usePageContext();
  const {
    shipmentsPanelHeader
  } = nthHookTranslations.useTranslations();
  const {
    query,
    mainShipmentId
  } = useOrderTracking__default["default"]();
  const orderTrackingData = query?.data?.orderTracking;
  const shipmentsToRender = getShipmentsToRender(orderTrackingData?.orders[0].shipments, mainShipmentId, trackingType);
  const [carouselScrolls, setCarouselScrolls] = React.useState(0);
  const eddCombos = shipmentsToRender.map(({
    displayDate
  }) => ({
    eddFormatTranslationKeys,
    displayDate: displayDate || '',
    dateOnly: false
  }));
  const formatEddQuery = useFormatEddQuery__default["default"]({
    skip: shipmentsToRender.length === 0,
    eddCombos
  });
  React.useEffect(() => {
    const orderNumber = orderNumbers && orderNumbers[0] || null;
    const componentHasRendered = orderTrackingData && getShipmentsToRender(orderTrackingData.orders[0].shipments, mainShipmentId, trackingType).length > 0;
    if (!componentHasRendered) return;
    logEvent({
      eventComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_PACK_SHIPMENTS,
      eventFeature: nthHookContextAnalytics.NarvarEventComponents.NTH_PACK_SHIPMENTS,
      eventType: nthHookContextAnalytics.NarvarEventTypes.COMPONENT_RENDER,
      feature: featureName,
      orderNumber,
      pageComponent: nthHookContextAnalytics.NarvarEventComponents.NTH_PACK_SHIPMENTS,
      pageOrderNumber: orderNumber,
      trackLayout: layout
    });
  },
  // TODO: Make this dependency array exhaustive
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [orderTrackingData]);
  if (!orderTrackingData || formatEddQuery.loading) return null;
  const shipmentsToRenderWithDisplayDateFormatted = shipmentsToRender.map((shipment, i) => ({
    ...shipment,
    displayDateFormatted: formatEddQuery.data?.formatEdd.formattedEdds[i] || []
  }));
  const shipmentsChunks = shipmentsToRenderWithDisplayDateFormatted.reduce((result, _, index, arr) => {
    if (index % 2 === 0) {
      const [itemChunk0, itemChunk1] = arr.slice(index, index + 2);
      if (itemChunk0) {
        result.push([itemChunk0, itemChunk1]);
      }
    }
    return result;
  }, []);
  if (shipmentsToRender.length === 0) return null;
  return /*#__PURE__*/React__default["default"].createElement(Panel__default["default"], {
    featureName: featureName,
    fixedPanelHeight: fixedPanelHeight,
    header: /*#__PURE__*/React__default["default"].createElement(PanelHeader__default["default"], {
      featureName: featureName
    }, mustache__default["default"].render(shipmentsPanelHeader || '', {
      shipmentsCount: shipmentsToRender.length
    }, undefined, renderOptions)),
    sInner: {
      padding: '1.5rem'
    }
  }, /*#__PURE__*/React__default["default"].createElement(Carousel__default["default"], {
    itemsCount: shipmentsChunks.length,
    slickProps: {
      dots: true,
      slidesToScroll: 1,
      slidesToShow: 1,
      infinite: false
    },
    carouselControlsContainerStyles: {
      borderTopColor: containerStyle && containerStyle.borderTopColor ? containerStyle.borderTopColor : '',
      borderTopStyle: containerStyle && containerStyle.borderTopStyle ? containerStyle.borderTopStyle : undefined,
      borderTopWidth: containerStyle && containerStyle.borderTopWidth ? containerStyle.borderTopWidth : '',
      padding: '.3rem 0',
      margin: 0
    },
    onArrowClick: () => setCarouselScrolls(carouselScrolls + 1)
  }, fixedPanelHeight ? shipmentsToRenderWithDisplayDateFormatted.map((shipment, i) => /*#__PURE__*/React__default["default"].createElement(ShipmentRow["default"], {
    key: `${shipment.shipmentId || ''}_${i}_shipmentrow`,
    landscape: landscape,
    index: i,
    featureName: featureName,
    shipment: shipment,
    trackingType: trackingType,
    carouselScrolls: carouselScrolls
  })) : shipmentsChunks.map((chunk, i) => /*#__PURE__*/React__default["default"].createElement("div", {
    key: `shipmentrow_${chunk[0].shipmentId || ''}_${chunk[1]?.shipmentId || ''}_${i}`
  }, /*#__PURE__*/React__default["default"].createElement(ShipmentRow["default"], {
    landscape: landscape,
    index: i * 2,
    key: `shipmentrow_${chunk[0].shipmentId || ''}_${i * 2}`,
    featureName: featureName,
    shipment: chunk[0],
    trackingType: trackingType,
    carouselScrolls: carouselScrolls
  }), chunk[1] && /*#__PURE__*/React__default["default"].createElement(ShipmentRow["default"], {
    landscape: landscape,
    index: i * 2 + 1,
    key: `shipmentrow_${chunk[1].shipmentId || ''}_${i * 2 + 1}`,
    featureName: featureName,
    shipment: chunk[1],
    trackingType: trackingType,
    carouselScrolls: carouselScrolls
  })))));
};
const ShipmentsWithData = nthHookTranslations.withTranslationsProvider(nthHookTheme.withThemeProvider(Shipments, {
  themeTokens: ['panelcontainerNoneNone']
}), {
  translationKeys: ['shipmentsPanelHeader']
});
const ShipmentsWithPrefetchedData = ({
  featureName,
  ...props
}) => /*#__PURE__*/React__default["default"].createElement(PrefetchData["default"], {
  featureName: featureName
}, /*#__PURE__*/React__default["default"].createElement(ShipmentsWithData, _extends__default["default"]({
  featureName: featureName
}, props)));

exports["default"] = ShipmentsWithPrefetchedData;
